<script setup lang="ts">
import SearchInput from '@/features/Search';
import FilledSquaresIcon from '@/shared/ui/Icons/FilledSquares.vue';
import { useNavMenu } from '@/shared/composables/useNavMenu';

const localePath = useLocalePath();
const { links, isAuth, setModalState, t } = useNavMenu('header');
</script>

<template>
  <div class="middle-header">
    <nuxt-link
      class="middle-header__item _logo"
      :to="localePath('/')"
      :aria-label="t('main')"
    >
      <NuxtImg class="middle-header__logo" src="/images/logo.webp" alt="logo" preload />
    </nuxt-link>
    <div class="middle-header__item _catalog">
      <nuxt-link
        class="middle-header__link _catalog"
        :aria-label="t('catalog')"
        :to="localePath('catalog')"
      >
        <FilledSquaresIcon class="middle-header__icon _catalog" />
        {{ t('catalog') }}
      </nuxt-link>
      <div class="middle-header__search">
        <ClientOnly>
          <SearchInput />
        </ClientOnly>
      </div>
    </div>
    <div class="middle-header__item">
      <ul class="middle-header__list">
        <li class="middle-header__list-item" v-for="link in links" :key="link.id">
          <button
            v-if="!isAuth"
            :class="['middle-header__button', link.classMod]"
            :aria-label="t('openAuthModal')"
            @click="setModalState('auth', true)"
          >
            <component :is="link.icon" class="middle-header__icon" />
            {{ link.name }}
          </button>
          <nuxt-link
            v-else
            :to="link.href"
            :class="['middle-header__link', link.classMod]"
            :aria-label="link.name"
          >
            <component :is="link.icon" class="middle-header__icon" />
            {{ link.name }}
          </nuxt-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="less">
.middle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  &__logo {
    width: 132px;
    height: 39px;
    margin-right: 30px;
  }

  &__item {
    &._logo {
      .a-reset();
    }

    &._catalog {
      display: inline-flex;
      align-items: center;
      gap: 25px;
      width: 100%;
      margin-right: 62px;
    }
  }

  &__search {
    width: 100%;
  }

  &__button {
    .button-reset();
  }

  &__button,
  &__link {
    display: inline-flex;
    gap: @size-x3;
    font-size: @size-x4;
    line-height: 130%;
    font-weight: 500;
    transition: color @A linear;

    &._catalog {
      .a-reset();

      display: flex;
      align-items: center;
      gap: @size-x2;
      line-height: 100%;
      font-weight: 600;
      color: white;
      background-color: @color-tango;
      border-radius: @size-x2;
      padding: @size-x3 @size-x4;
      height: 43px;
      transition: background-color @A linear;

      &:hover {
        background-color: rgba(@color-tango, 0.75);
      }
    }

    &:hover:not(._catalog) {
      color: @color-tango;
    }

    &:hover:not(._catalog, ._favorite) .middle-header__icon path {
      transition: fill @A linear;
      fill: @color-tango;
    }

    &:hover._favorite .middle-header__icon path {
      transition: stroke @A linear;
      stroke: @color-carnation;
    }

    @media @desktop {
      font-size: 15px;
    }
  }

  &__icon {
    width: @size-x5;
    height: @size-x5;

    &._catalog {
      width: @size-x4;
      height: @size-x4;
    }
  }

  &__list {
    display: inline-flex;
    align-items: center;
    gap: @size-x6;
  }

  &__list-item {
    display: inline-flex;
  }
}
</style>
